import { useEffect, useRef, useState } from "react";
import { QrCode, X, Camera } from "lucide-react";

/**
 * QRScannerButton — small button that opens a modal with camera preview
 * and scans a QR / barcode. On success calls onScan(text).
 * Requires HTTPS + camera permission.
 */
export default function QRScannerButton({ onScan, testid = "qr-scan-btn" }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const scannerRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;
        const id = "qr-scanner-" + Math.random().toString(36).slice(2, 8);
        if (scannerRef.current) scannerRef.current.id = id;
        const instance = new Html5Qrcode(id, { verbose: false });
        instanceRef.current = instance;
        await instance.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 },
          (decodedText) => {
            try {
              instance.stop().then(() => instance.clear()).catch((err) => console.warn("qr stop error", err));
            } catch (err) {
              console.warn("qr stop threw", err);
            }
            setOpen(false);
            onScan?.(decodedText.trim());
          },
          () => { /* per-frame decode errors are expected while aiming; ignore */ }
        );
      } catch (e) {
        setError(e?.message || "Não foi possível acessar a câmera. Verifique a permissão.");
      }
    })();
    return () => {
      cancelled = true;
      const inst = instanceRef.current;
      if (inst) {
        try {
          inst.stop().then(() => inst.clear()).catch((err) => console.warn("qr cleanup stop error", err));
        } catch (err) {
          console.warn("qr cleanup threw", err);
        }
        instanceRef.current = null;
      }
    };
  }, [open, onScan]);

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => setOpen(true)}
        title="Escanear QR / Código de Barras"
        data-testid={testid}
      >
        <QrCode size={14} />
      </button>
      {open && (
        <div className="modal-backdrop" onClick={() => setOpen(false)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2 label-caps"><Camera size={14} /> Escanear código</div>
              <button className="btn btn-ghost" onClick={() => setOpen(false)}><X size={16} /></button>
            </div>
            <div className="p-4">
              {error ? (
                <div className="p-3 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>
                  {error}
                </div>
              ) : (
                <>
                  <div ref={scannerRef} id="qr-scanner-live" style={{ width: "100%", minHeight: 300, background: "#000" }} />
                  <div className="text-xs mt-3 text-center" style={{ color: "var(--text-mute)" }}>
                    Aponte a câmera para o QR / Barras do equipamento. A leitura é automática.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
